import React from 'react';
import {
	FormattedMessage as FormattedMessageNamespace,
	type MessageDescriptor,
} from 'react-intl-next';

import {
	type AvailableProductsResponse,
	type AvailableProduct,
	type SwitcherProductType,
	type CurrentSite,
	type MapUrl,
	type ProductConfigurationMap,
	type AvailableProductDetails,
} from '../../types';
import messages from './messages';
import { type CustomLink, type SwitcherChildItem } from '../../types';
import { daysBetween } from './days-between';
import { type AppIconKey } from '../../ui/primitives/app-icon';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	type ExperimentNudgeToJira,
	type Cohort,
	getIsEligibleForNudgeToJira,
	type Viewer,
} from './nudge-to-jira';

export const DAYS_AUTO_PROVISIONED_ITEM_CONSIDERED_NEW = 14;
export const DAYS_END_USER_ACTIVE_PRODUCT_CONSIDERED_NEW = 14;

interface MessagesDict {
	[index: string]: MessageDescriptor;
}

export type SwitcherItemType = {
	key: string;
	label: JSX.Element | string;
	iconKey: AppIconKey;
	href?: string;
	productType?: SwitcherProductType;
	description?: JSX.Element | string | null;
	iconUrl?: string;
	childItems?: SwitcherChildItem[];
	analyticsAttributes?: { [key: string]: string };
	shouldShowNewLozenge?: boolean;
	shouldShowNewBoldLozenge?: boolean;
	shouldShowTryLozenge?: boolean;
	autoProvisionDate?: string;
	isProductInactive?: boolean;
	ariaLabel?: MessageDescriptor;
	isCompanyHubPublishedAndPremium?: boolean;
	companyHubName?: string;
	experimentNudgeToJira?: ExperimentNudgeToJira;
};

export type ProductSwitcherItemType = SwitcherItemType & {
	ordinal: number;
	productType: SwitcherProductType;
};

export const OBJECT_TYPE_TO_LABEL_MAP: MessagesDict = {
	// pf-activity specific object types. To be removed when pf-activity is deprecated
	'jira-project': messages.jiraProject,
	'confluence-space': messages.confluenceSpace,
	jiraProject: messages.jiraProject,
	confluenceSpace: messages.confluenceSpace,
};

export const getObjectTypeLabel = (type: string): JSX.Element | string => {
	return OBJECT_TYPE_TO_LABEL_MAP[type] ? (
		<FormattedMessageNamespace {...OBJECT_TYPE_TO_LABEL_MAP[type]} />
	) : (
		type
	);
};

export interface ConnectedSite {
	avatar: string | null;
	product: AvailableProduct;
	isCurrentSite: boolean;
	siteName: string;
	siteUrl: string;
	shouldShowNewLozenge?: boolean;
	shouldShowNewBoldLozenge?: boolean;
	shouldShowTryLozenge?: boolean;
	autoProvisionDate?: string;
	isProductInactive?: boolean;
	isHighlightJwmInSwitcherExtendedAudienceEnabled?: boolean;
	isCompanyHubPublishedAndPremium?: boolean;
	companyHubName?: string;
	experimentNudgeToJira?: ExperimentNudgeToJira;
}

export const getProductSiteUrl = (
	availableProductMap: ProductConfigurationMap,
	product: AvailableProduct,
	siteUrl: string,
): string => {
	if (product.url) {
		return product.url;
	}

	if (availableProductMap[product.productType]?.href) {
		return siteUrl + availableProductMap[product.productType].href;
	}

	return siteUrl;
};

const getAvailableProductLinkFromSiteProduct = (
	productConfigurationMap: ProductConfigurationMap,
	connectedSites: ConnectedSite[],
	mapUrl: MapUrl,
	rebrand: boolean = false,
): ProductSwitcherItemType | null => {
	const topSite =
		connectedSites.find((site) => site.isCurrentSite) ||
		connectedSites.sort((a, b) => a.siteName.localeCompare(b.siteName))[0];
	const productType = topSite.product.productType;
	const productLinkProperties: AvailableProductDetails | undefined =
		productConfigurationMap[productType];

	if (!productLinkProperties) {
		return null;
	}

	return {
		...productLinkProperties,
		label:
			rebrand && productLinkProperties.rebrandedLabel
				? productLinkProperties.rebrandedLabel
				: productLinkProperties.label,
		iconKey:
			rebrand && productLinkProperties.rebrandedIconKey
				? productLinkProperties.rebrandedIconKey
				: productLinkProperties.iconKey,
		key: productType + topSite.siteName,
		href: mapUrl(
			getProductSiteUrl(productConfigurationMap, topSite.product, topSite.siteUrl),
			productType,
		),
		iconUrl: productLinkProperties.iconUrl,
		description: topSite.siteName,
		productType,
		childItems:
			connectedSites.length > 1
				? connectedSites
						.map((site) => ({
							href: mapUrl(
								getProductSiteUrl(productConfigurationMap, site.product, site.siteUrl),
								productType,
							),
							label: site.siteName,
							avatar: site.avatar,
						}))
						.sort((a, b) => a.label.localeCompare(b.label))
				: [],
		shouldShowNewLozenge: topSite.shouldShowNewLozenge,
		shouldShowNewBoldLozenge: topSite.shouldShowNewBoldLozenge,
		shouldShowTryLozenge: topSite.shouldShowTryLozenge,
		autoProvisionDate: topSite.autoProvisionDate,
		isProductInactive: topSite.isProductInactive,
		isCompanyHubPublishedAndPremium: topSite.isCompanyHubPublishedAndPremium,
		companyHubName: topSite.companyHubName,
		experimentNudgeToJira: topSite.experimentNudgeToJira,
	};
};

const isNewlyAutoProvisioned = (product: AvailableProduct) => {
	const { autoProvisionDate } = product;
	let isConsideredNew = false;
	if (autoProvisionDate) {
		const days = daysBetween(new Date(autoProvisionDate), new Date());
		isConsideredNew = days <= DAYS_AUTO_PROVISIONED_ITEM_CONSIDERED_NEW && days >= 0;
	}
	return isConsideredNew;
};

const getIsRecentEndUserActivatedProduct = (product: AvailableProduct) => {
	const { endUserActivationDate, productType } = product;
	if (
		!endUserActivationDate ||
		!['CONFLUENCE', 'JIRA_SERVICE_DESK', 'JIRA_PRODUCT_DISCOVERY', 'LOOM'].includes(productType)
	) {
		return false;
	}

	const days = daysBetween(new Date(endUserActivationDate), new Date());
	return days <= DAYS_END_USER_ACTIVE_PRODUCT_CONSIDERED_NEW && days >= 0;
};

export const getAvailableProductLinks = (
	productConfigurationMap: ProductConfigurationMap,
	availableProducts: AvailableProductsResponse,
	cloudId: string | null | undefined,
	mapUrl: MapUrl,
	orgId?: string,
	viewer?: Viewer,
): ProductSwitcherItemType[] => {
	const productsMap: { [key: string]: ConnectedSite[] } = {};
	let shouldRemoveJWM = false;

	const isNudgeToJiraAATestEnabled = FeatureGates.checkGate(
		'nudge_to_jira_in_switcher_aa_test_gate',
	);
	const isNudgeToJiraEnabled = FeatureGates.checkGate('nudge_to_jira_in_switcher_gate');

	availableProducts.sites.forEach((site) => {
		const { availableProducts, avatar, displayName, url } = site;

		// only apply org level filtering if org data is available
		if ('org' in site && site.org && orgId) {
			if (orgId !== site.org.id) {
				return;
			}
		}

		availableProducts.forEach((product) => {
			const { activationDate, autoProvisionDate, isCompanyHubPublishedAndPremium, companyHubName } =
				product;
			let productKey = product.productType;

			if (['JIRA_SOFTWARE', 'JIRA_PRODUCT_DISCOVERY', 'JIRA_SERVICE_DESK'].includes(productKey)) {
				shouldRemoveJWM = true;
			}

			// if a site has JWM only and no other Jiras, JWM needs to appear under "Jira"
			// to ensure that this happens, we set JSM's productKey to 'JIRA_SOFTWARE'
			// this will ensure that JWM is not removed, even if another site has JSW/JPD/JSM
			if (
				productKey === 'JIRA_BUSINESS' &&
				!availableProducts.some(({ productType }) =>
					['JIRA_SOFTWARE', 'JIRA_PRODUCT_DISCOVERY', 'JIRA_SERVICE_DESK'].includes(productType),
				)
			) {
				productKey = 'JIRA_SOFTWARE';
			}

			if (!productsMap[productKey]) {
				productsMap[productKey] = [];
			}

			const isRecentEndUserActivatedProduct = getIsRecentEndUserActivatedProduct(product);

			const isCurrentSite =
				Boolean(cloudId) && site.cloudId?.toLowerCase() === cloudId?.toLowerCase();

			const shouldShowNewLozenge =
				isRecentEndUserActivatedProduct ||
				(product.productType !== 'CONFLUENCE' && isNewlyAutoProvisioned(product));

			const shouldShowTryLozenge =
				product.productType === 'CONFLUENCE' && isNewlyAutoProvisioned(product);

			// TBLZ-2123: Growth - Trailblazers experiment - More info at go/trailblazerexperiments
			const experimentNudgeToJira =
				(isNudgeToJiraAATestEnabled || isNudgeToJiraEnabled) &&
				product.productType === 'JIRA_SOFTWARE'
					? {
							attributes: {
								activationDate,
								hasAdminAccess: site.adminAccess,
								product: viewer?.product,
								subproduct: viewer?.subproduct,
							},
							isEligibleForNudgeToJiraAATest: undefined,
							nudgeToJiraCohort: undefined,
						}
					: undefined;

			productsMap[productKey].push({
				product,
				isCurrentSite,
				isCompanyHubPublishedAndPremium,
				companyHubName,
				shouldShowNewLozenge,
				shouldShowTryLozenge,
				autoProvisionDate,
				siteName: displayName,
				siteUrl: url,
				avatar,
				experimentNudgeToJira,
			});
		});
	});

	// TBLZ-2123: Growth - Trailblazers experiment - More info at go/trailblazerexperiments
	if (
		(isNudgeToJiraAATestEnabled || isNudgeToJiraEnabled) &&
		productsMap['JIRA_SOFTWARE']?.length === 1
	) {
		const [connectedSite] = productsMap['JIRA_SOFTWARE'];

		const isEligibleForNudgeToJira = getIsEligibleForNudgeToJira({
			connectedSite,
			cloudId,
			viewer,
		});

		// AA Test
		if (isNudgeToJiraAATestEnabled) {
			connectedSite.experimentNudgeToJira = {
				...connectedSite.experimentNudgeToJira,
				isEligibleForNudgeToJiraAATest: isEligibleForNudgeToJira,
			};
		}

		// Experiment
		if (isEligibleForNudgeToJira && isNudgeToJiraEnabled) {
			const nudgeToJiraCohort = FeatureGates.getExperimentValue<Cohort>(
				'nudge_to_jira_in_switcher',
				'cohort',
				'not-enrolled',
			);

			connectedSite.shouldShowNewBoldLozenge = nudgeToJiraCohort === 'variation';
			connectedSite.experimentNudgeToJira = {
				...connectedSite.experimentNudgeToJira,
				nudgeToJiraCohort,
			};
		}
	}
	// END TBLZ-2123: Growth - Trailblazers experiment - More info at go/trailblazerexperiments

	const items = [];
	for (const productType in productsMap) {
		if (productType === 'JIRA_BUSINESS' && shouldRemoveJWM) {
			continue;
		}

		const rebrand = productType === 'JIRA_SOFTWARE' || productType === 'JIRA_BUSINESS';
		// Rebranding Rules:
		// 1. When both Jira Work Management (JWM) and Jira Software (JSW) appear, rebrand both and only show one link
		// 2. When only one of them (JWM or JSW) appears, rebrand both
		// for more info: https://hello.atlassian.net/wiki/spaces/Spork/pages/4059534637/Spork+Rebranding+Appswitcher+behaviour

		const connectedSites = productsMap[productType];
		const link = getAvailableProductLinkFromSiteProduct(
			productConfigurationMap,
			connectedSites,
			mapUrl,
			rebrand,
		);

		if (link) {
			items.push(link);
		}
	}

	return items.sort((a, b) => {
		return a.ordinal - b.ordinal;
	});
};

export const getCustomLinkItems = (
	list: Array<CustomLink>,
	currentSite: CurrentSite,
): SwitcherItemType[] => {
	const defaultProductCustomLinks = [
		`${currentSite.url}/secure/MyJiraHome.jspa`,
		`${currentSite.url}/wiki/`,
	];
	return list
		.filter((customLink) => defaultProductCustomLinks.indexOf(customLink.link) === -1)
		.map((customLink) => ({
			key: customLink.key,
			label: customLink.label,
			iconKey: 'custom-link',
			href: customLink.link,
			analyticsAttributes: {
				linkType: customLink.local ? 'customLink' : 'applink',
			},
		}));
};
